/**
 * Freehand strokes ("Zeichnen"): input smoothing, conversion into a `path`
 * element (path data normalised to the element box) and hit testing for the
 * eraser. Pure functions, shared by the editor and tests.
 */

type Point = { x: number; y: number };
type Box = { x: number; y: number; width: number; height: number; rotation: number };

/** Brushes of the drawing tool (the eraser is a tool mode, not stored). */
export const PATH_BRUSHES = ["pen", "marker", "highlighter"] as const;
export type PathBrush = (typeof PATH_BRUSHES)[number];

/** Longest path data of a drawn stroke. */
export const PATH_DATA_MAX_LENGTH = 40_000;

const round = (value: number) => Math.round(value * 100) / 100;

function distanceToSegment(point: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

/** Ramer–Douglas–Peucker: drops points closer than `tolerance` to the simplified line. */
export function simplifyPoints(points: Point[], tolerance: number): Point[] {
  if (points.length <= 2) return points.slice();
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    let maxDistance = 0;
    let index = -1;
    for (let i = first + 1; i < last; i++) {
      const distance = distanceToSegment(points[i]!, points[first]!, points[last]!);
      if (distance > maxDistance) {
        maxDistance = distance;
        index = i;
      }
    }
    if (index !== -1 && maxDistance > tolerance) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }
  return points.filter((_, i) => keep[i] === 1);
}

/** Drops consecutive points closer than `minDistance` (pointer jitter). */
export function dedupePoints(points: Point[], minDistance: number): Point[] {
  const out: Point[] = [];
  for (const point of points) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(point.x - last.x, point.y - last.y) >= minDistance) out.push(point);
  }
  const end = points[points.length - 1];
  if (end && out.length > 1 && out[out.length - 1] !== end) out[out.length - 1] = end;
  return out;
}

const fmt = (point: Point) => `${round(point.x)} ${round(point.y)}`;

/**
 * Smooth path data through the points: quadratic curves with the input
 * points as control points and the midpoints between them as anchors, so the
 * curve is continuous in direction. One point gives a dot (round caps).
 */
export function smoothPathData(points: Point[]): string {
  if (points.length === 0) return "";
  const first = points[0]!;
  if (points.length === 1) return `M${fmt(first)} L${fmt(first)}`;
  if (points.length === 2) return `M${fmt(first)} L${fmt(points[1]!)}`;
  let d = `M${fmt(first)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const current = points[i]!;
    const next = points[i + 1]!;
    const mid = { x: (current.x + next.x) / 2, y: (current.y + next.y) / 2 };
    d += ` Q${fmt(current)} ${fmt(mid)}`;
  }
  d += ` L${fmt(points[points.length - 1]!)}`;
  return d;
}

export type NormalizedStroke = {
  /** Element box in page coordinates (the stroke plus half its width all around). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Path data in box coordinates (viewBox = 0 0 width height). */
  d: string;
  viewBox: [number, number];
};

/**
 * Turns raw pointer samples (page coordinates) into a smoothed stroke whose
 * path data is relative to its own box. The viewBox equals the box size at
 * creation; scaling the element later scales the stroke with it.
 */
export function normalizeStroke(
  raw: Point[],
  strokeWidth: number,
  options: { tolerance?: number } = {},
): NormalizedStroke | null {
  if (raw.length === 0) return null;
  const tolerance = options.tolerance ?? Math.max(0.5, strokeWidth * 0.15);
  const points = simplifyPoints(dedupePoints(raw, tolerance / 2), tolerance);
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  const pad = strokeWidth / 2;
  const x = minX - pad;
  const y = minY - pad;
  const width = Math.max(1, round(maxX - minX + pad * 2));
  const height = Math.max(1, round(maxY - minY + pad * 2));
  const local = points.map((point) => ({ x: point.x - x, y: point.y - y }));
  return {
    x: round(x),
    y: round(y),
    width,
    height,
    d: smoothPathData(local),
    viewBox: [width, height],
  };
}

/** Anchor and control points of path data (pairs of numbers), for hit testing. */
export function pathDataPoints(d: string): Point[] {
  const numbers = d.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi)?.map(Number) ?? [];
  const out: Point[] = [];
  for (let i = 0; i + 1 < numbers.length; i += 2) out.push({ x: numbers[i]!, y: numbers[i + 1]! });
  return out;
}

/** Shortest distance from a point to the polyline through `points`. */
export function distanceToPolyline(point: Point, points: Point[]): number {
  if (points.length === 0) return Number.POSITIVE_INFINITY;
  if (points.length === 1) return Math.hypot(point.x - points[0]!.x, point.y - points[0]!.y);
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < points.length; i++) {
    best = Math.min(best, distanceToSegment(point, points[i - 1]!, points[i]!));
  }
  return best;
}

/**
 * Whether a point (page coordinates) within `radius` touches a path element
 * drawn in `box` (page-space box, possibly rotated).
 */
export function pathHit(
  element: { d: string; viewBox: [number, number]; strokeWidth: number },
  box: Box,
  point: Point,
  radius: number,
): boolean {
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  const angle = (-box.rotation * Math.PI) / 180;
  const dx = point.x - cx;
  const dy = point.y - cy;
  const px = cx + dx * Math.cos(angle) - dy * Math.sin(angle) - box.x;
  const py = cy + dx * Math.sin(angle) + dy * Math.cos(angle) - box.y;
  const sx = box.width / element.viewBox[0];
  const sy = box.height / element.viewBox[1];
  const local = { x: px / sx, y: py / sy };
  const scale = Math.min(sx, sy) || 1;
  const reach = radius / scale + element.strokeWidth / 2;
  return distanceToPolyline(local, pathDataPoints(element.d)) <= reach;
}
